import React, { useEffect, useState } from 'react';
import { auth } from '../../../firebase/config';
import Button from '../../../components/Button';
import Toast from '../../../components/Toast';
import { FiPlus, FiSearch, FiEye, FiTrendingUp, FiUsers, FiRefreshCw } from 'react-icons/fi';
import Icon from '../../../components/Icon';
import AddManagerModal from '../components/AddManagerModal/index';
import ManagerDetailsModal from '../components/ManagerDetailsModal';
import { fetchManagers, fetchManagerBankId, updateManager, type Manager } from '../service/managersService';
import Table, { Column } from '../../../components/Table';

export default function Managers() {
    const [managers, setManagers] = useState<Manager[]>([]);
    const [filtered, setFiltered] = useState<Manager[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' | 'warning' }>({
        show: false,
        message: '',
        type: 'success',
    });
    const [selectedManager, setSelectedManager] = useState<Manager | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
    const [managerTypes, setManagerTypes] = useState<string[]>([]);
    const [bankId, setBankId] = useState<string | null>(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 20;

    const uid = auth.currentUser?.uid;
    const userRole = sessionStorage.getItem('role'); // 'bank' or 'pso'
    const isBankUser = userRole === 'bank';

    useEffect(() => {
        if (!uid) return;

        const loadData = async () => {
            setLoading(true);
            try {
                const fetchedBankId = await fetchManagerBankId(uid, isBankUser);
                if (fetchedBankId) {
                    setBankId(fetchedBankId);
                    const managersData = await fetchManagers(fetchedBankId);
                    setManagers(managersData);
                    setFiltered(managersData);

                    // Extract unique manager types
                    const typesSet = new Set<string>();
                    managersData.forEach(manager => {
                        if (manager.type) {
                            typesSet.add(manager.type);
                        }
                    });
                    setManagerTypes(Array.from(typesSet));
                }
            } catch (err: any) {
                showToast('Failed to load managers data', 'error');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [uid, isBankUser]);

    const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
        setToast({ show: true, message, type });
    };

    useEffect(() => {
        let list = managers;

        // Apply filter based on selected manager type
        if (selectedFilter && selectedFilter !== 'All') {
            list = list.filter(manager => manager.type === selectedFilter);
        }

        // Apply search term
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            list = list.filter(
                (manager) =>
                    manager.name?.toLowerCase().includes(term) ||
                    manager.mobile?.toLowerCase().includes(term) ||
                    manager.email?.toLowerCase().includes(term)
            );
        }

        // Sort by name alphabetically
        list.sort((a, b) => {
            const nameA = (a.name || '').toString();
            const nameB = (b.name || '').toString();
            return nameA.localeCompare(nameB);
        });

        setFiltered(list);
        setCurrentPage(1);
    }, [searchTerm, managers, selectedFilter]);

    const handleReset = () => {
        setSearchTerm('');
        setSelectedFilter(null);
        setFiltered(managers);
        setCurrentPage(1);
    };

    const handleStatCardClick = (managerType: string) => {
        setSelectedFilter(selectedFilter === managerType ? null : managerType);
        setCurrentPage(1);
    };

    const openDetailsModal = (manager: Manager) => {
        setSelectedManager(manager);
        setShowDetailsModal(true);
    };

    const handleManagerAdded = async () => {
        if (!bankId) return;
        setLoading(true);
        try {
            const managersData = await fetchManagers(bankId);
            setManagers(managersData);

            // Extract unique manager types
            const typesSet = new Set<string>();
            managersData.forEach(m => {
                if (m.type) {
                    typesSet.add(m.type);
                }
            });
            setManagerTypes(Array.from(typesSet));

            setCurrentPage(1);
            showToast('✅ Manager added successfully!', 'success');
            setShowAddModal(false);
        } catch (err: any) {
            console.error('Error refreshing managers:', err);
            showToast('❌ Failed to refresh managers', 'error');
        } finally {
            setLoading(false);
        }
    };

    const totalPages = Math.ceil(filtered.length / perPage);
    const paginatedData = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

    // Calculate manager statistics by type
    const totalManagers = managers.length;
    const managerTypeCounts = React.useMemo(() => {
        const counts: Record<string, number> = {};
        managers.forEach(manager => {
            const type = manager.type || 'Unknown';
            counts[type] = (counts[type] || 0) + 1;
        });
        return counts;
    }, [managers]);

    const typeLabels: Record<string, string> = {
        'BranchManager': 'Branch Manager',
        'OperationalManager': 'Manager Operation',
        'AreaOperationManager': 'Area Operation Manager',
    };

    const columns: Column<Manager>[] = [
        { header: 'Name', accessor: 'name', width: '25%', render: (manager) => <span className="font-medium text-gray-900 dark:text-gray-100">{manager.name || 'N/A'}</span> },
        { header: 'Mobile', accessor: 'mobile', width: '20%', className: 'text-gray-600 dark:text-gray-400' },
        { header: 'Email', accessor: 'email', width: '30%', className: 'text-gray-600 dark:text-gray-400' },
        {
            header: 'Type',
            accessor: 'type',
            width: '20%',
            render: (manager) => manager.type ? (
                <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                    {typeLabels[manager.type] || manager.type}
                </span>
            ) : (
                <span className="text-sm text-gray-500 dark:text-gray-400">N/A</span>
            )
        },
        {
            header: 'Action',
            width: '10%',
            align: 'center',
            render: (manager) => (
                <button
                    onClick={() => openDetailsModal(manager)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 dark:focus:ring-blue-400"
                    aria-label="View manager details"
                >
                    <Icon icon={FiEye} className="h-4 w-4" />
                </button>
            )
        }
    ];

    const headerActions = isBankUser ? (
        <Button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2"
        >
            <Icon icon={FiPlus} className="h-4 w-4" />
            Add Manager
        </Button>
    ) : null;

    const filters = (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative flex-1">
                <Icon
                    icon={FiSearch}
                    className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                />
                <input
                    type="text"
                    placeholder="Search by name, mobile, or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                />
            </div>
            <div className="relative">
                <select
                    value={selectedFilter || 'All'}
                    onChange={(e) => setSelectedFilter(e.target.value === 'All' ? null : e.target.value)}
                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-4 pr-10 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                >
                    <option value="All">All Types</option>
                    {managerTypes.map((type) => (
                        <option key={type} value={type}>
                            {typeLabels[type] || type}
                        </option>
                    ))}
                </select>
            </div>
            {searchTerm && (
                <Button
                    variant="secondary"
                    onClick={handleReset}
                    className="text-sm whitespace-nowrap flex items-center gap-2"
                >
                    <Icon icon={FiRefreshCw} className="h-4 w-4" />
                    Reset
                </Button>
            )}
        </div>
    );

    if (loading && managers.length === 0) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                    <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Loading managers...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="space-y-6">
                {/* Statistics Cards - Manager Types */}
                <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${managerTypes.length > 2 ? 'lg:grid-cols-4' : 'lg:grid-cols-' + Math.min(managerTypes.length + 1, 4)}`}>
                    {/* Total Managers Card */}
                    <div className="rounded-xl border-2 border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-black/30">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Managers</p>
                                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">{totalManagers}</p>
                            </div>
                            <div className="rounded-full bg-gray-200 p-3 dark:bg-gray-700">
                                <Icon icon={FiTrendingUp} className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                            </div>
                        </div>
                        {/* Mini Graph */}
                        <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                            <div className="h-full w-full bg-gray-500 dark:bg-gray-500"></div>
                        </div>
                    </div>

                    {/* Manager Type Cards */}
                    {managerTypes.map((type) => {
                        const count = managerTypeCounts[type] || 0;
                        const percentage = totalManagers > 0 ? (count / totalManagers) * 100 : 0;
                        const typeLabel = typeLabels[type] || type;
                        return (
                            <div
                                key={type}
                                onClick={() => handleStatCardClick(type)}
                                className={`rounded-xl border-2 cursor-pointer transition-all p-6 shadow-sm ${selectedFilter === type
                                    ? 'border-gray-900 bg-gray-100 dark:border-gray-100 dark:bg-gray-800'
                                    : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-black/30 hover:border-gray-300 dark:hover:border-gray-600'
                                    }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{typeLabel}</p>
                                        <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">{count}</p>
                                        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">{percentage.toFixed(1)}% of total</p>
                                    </div>
                                    <div className="rounded-full bg-gray-200 p-3 dark:bg-gray-700">
                                        <Icon icon={FiUsers} className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                                    </div>
                                </div>
                                {/* Mini Graph */}
                                <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                                    <div
                                        className="h-full bg-gray-500 dark:bg-gray-500 transition-all duration-500"
                                        style={{ width: `${percentage}%` }}
                                    ></div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Main Table Container */}
                <Table
                    title="Managers"
                    description="Manage and view all manager information"
                    data={paginatedData}
                    columns={columns}
                    keyExtractor={(manager) => manager.id}
                    loading={loading}
                    emptyMessage={searchTerm || selectedFilter ? 'No managers found matching your search' : 'No managers available'}
                    headerActions={headerActions}
                    filters={filters}
                    pagination={{
                        currentPage,
                        totalPages,
                        onPageChange: setCurrentPage
                    }}
                />
            </div>

            {/* Toast */}
            {toast.show && (
                <div className="pointer-events-none fixed right-4 top-4 z-50">
                    <div className="pointer-events-auto">
                        <Toast
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToast({ ...toast, show: false })}
                        />
                    </div>
                </div>
            )}

            {/* Modals */}
            <AddManagerModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={handleManagerAdded}
                bankId={bankId || undefined}
            />

            <ManagerDetailsModal
                isOpen={showDetailsModal}
                onClose={() => {
                    setShowDetailsModal(false);
                    setSelectedManager(null);
                }}
                manager={selectedManager}
                bankId={bankId || undefined}
                onUpdate={async () => {
                    if (!bankId) return;
                    try {
                        const managersData = await fetchManagers(bankId);
                        setManagers(managersData);
                        // Extract unique manager types
                        const typesSet = new Set<string>();
                        managersData.forEach(manager => {
                            if (manager.type) {
                                typesSet.add(manager.type);
                            }
                        });
                        setManagerTypes(Array.from(typesSet));
                        showToast('✅ Manager updated successfully!', 'success');
                    } catch (err: any) {
                        showToast('❌ Failed to refresh managers', 'error');
                    }
                }}
            />
        </>
    );
}

